import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  Cog6ToothIcon,
  FolderIcon,
  HomeIcon,
  MoonIcon,
  ShieldCheckIcon,
  SunIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import logo from '../assets/images/logo.svg';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { Avatar } from './ui';

const Layout: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, appUser, isAdmin, signOut } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMobileOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const navigation = [
    { name: t('nav.dashboard'), href: '/', icon: HomeIcon, end: true },
    { name: t('nav.projects'), href: '/projects', icon: FolderIcon, end: false },
    ...(isAdmin ? [{ name: t('nav.access'), href: '/access', icon: ShieldCheckIcon, end: false }] : []),
    { name: t('nav.settings'), href: '/settings', icon: Cog6ToothIcon, end: false },
  ];

  const displayName = appUser?.displayName || user?.displayName || user?.email || '';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <header className="sticky top-0 z-40 border-b border-gray-200/80 bg-white/90 backdrop-blur dark:border-gray-800 dark:bg-gray-950/85">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-6 px-4 sm:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2.5">
            <img src={logo} alt="" className="h-8 w-8" />
            <span className="text-[17px] font-bold tracking-tight text-gray-900 dark:text-white">AppTransfer</span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex" aria-label={t('nav.main')}>
            {navigation.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                end={item.end}
                className={({ isActive }) =>
                  `rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-white'
                      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                  }`
                }
              >
                {item.name}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              onClick={toggleTheme}
              className="btn btn-ghost btn-icon"
              aria-label={theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
              title={theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
            >
              {theme === 'dark' ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
            </button>
            <button
              type="button"
              onClick={() => setLanguage(language === 'en' ? 'hu' : 'en')}
              className="btn btn-ghost btn-sm hidden sm:inline-flex"
              title={t('settings.language')}
            >
              {language === 'hu' ? 'EN' : 'HU'}
            </button>

            <div className="relative hidden md:block" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                className="ml-1 flex items-center gap-2 rounded-full p-0.5 pr-1 hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label={t('nav.account')}
              >
                <Avatar name={displayName} photoURL={user?.photoURL} size="sm" />
              </button>
              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-64 animate-fadeIn rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg dark:border-gray-800 dark:bg-gray-900"
                >
                  <div className="px-3 py-2.5">
                    <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{displayName}</p>
                    <p className="truncate text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
                    {isAdmin && <span className="badge badge-brand mt-2">{t('common.admin')}</span>}
                  </div>
                  <div className="my-1 h-px bg-gray-100 dark:bg-gray-800" />
                  <Link
                    to="/settings"
                    role="menuitem"
                    className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
                  >
                    <Cog6ToothIcon className="h-4 w-4" />
                    {t('nav.settings')}
                  </Link>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={signOut}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4" />
                    {t('auth.signOut')}
                  </button>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="btn btn-ghost btn-icon md:hidden"
              aria-label={t('nav.menu')}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="border-t border-gray-200 bg-white px-4 pb-4 pt-2 dark:border-gray-800 dark:bg-gray-950 md:hidden">
            <nav className="space-y-1" aria-label={t('nav.main')}>
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    end={item.end}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] font-medium ${
                        isActive
                          ? 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-white'
                          : 'text-gray-600 dark:text-gray-300'
                      }`
                    }
                  >
                    <Icon className="h-5 w-5" />
                    {item.name}
                  </NavLink>
                );
              })}
            </nav>
            <div className="mt-3 flex items-center gap-3 border-t border-gray-100 px-3 pt-4 dark:border-gray-800">
              <Avatar name={displayName} photoURL={user?.photoURL} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{displayName}</p>
                <p className="truncate text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
              </div>
              <button
                type="button"
                onClick={() => setLanguage(language === 'en' ? 'hu' : 'en')}
                className="btn btn-ghost btn-sm"
              >
                {language === 'hu' ? 'EN' : 'HU'}
              </button>
            </div>
            <button type="button" onClick={signOut} className="btn btn-secondary mt-3 w-full">
              <ArrowRightOnRectangleIcon className="h-4 w-4" />
              {t('auth.signOut')}
            </button>
          </div>
        )}
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">{children}</main>
    </div>
  );
};

export default Layout;
